import { useEffect, useRef, useState } from 'react'
import { Bell, Moon, Sun, Volume2, VolumeX } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { useTheme } from '../../hooks/useTheme'
import { useColors } from '../../hooks/useColors'
import { useNotificationStore } from '../../store/notificationStore'

interface TopbarProps {
  title: string
  subtitle: string
  onMenuClick?: () => void
}

const SOUND_MUTE_KEY = 'rapidry_admin_sound_muted'

const useSoundMutePreference = () => {
  const [muted, setMuted] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(SOUND_MUTE_KEY) === '1'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      sessionStorage.setItem(SOUND_MUTE_KEY, muted ? '1' : '0')
    } catch {
      /* sessionStorage unavailable - preference stays in-memory only */
    }
  }, [muted])

  return [muted, setMuted] as const
}

const getInitials = (name: string | undefined) => {
  if (!name) {
    return 'AD'
  }

  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

export default function Topbar({ title, subtitle, onMenuClick }: TopbarProps) {
  const user = useAuthStore((state) => state.user)
  const { isDark, toggle } = useTheme()
  const colors = useColors()
  const [soundMuted, setSoundMuted] = useSoundMutePreference()

  const navigate = useNavigate()
  const notifications = useNotificationStore((s) => s.notifications)
  const unreadCount = useNotificationStore((s) => s.unreadCount)
  const markAllRead = useNotificationStore((s) => s.markAllRead)
  const clearNotifications = useNotificationStore((s) => s.clear)
  const [bellOpen, setBellOpen] = useState(false)
  const bellRef = useRef<HTMLDivElement | null>(null)

  // Close the notifications dropdown on outside-click / Escape.
  useEffect(() => {
    if (!bellOpen) return
    const onDoc = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) setBellOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setBellOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [bellOpen])

  const toggleBell = () => {
    setBellOpen((open) => {
      const next = !open
      if (next) markAllRead() // opening clears the unread badge
      return next
    })
  }

  return (
    <header
      className="admin-topbar"
      style={{
        height: '64px',
        background: colors.bgPrimary,
        borderBottom: `1px solid ${colors.border}`,
        padding: '0 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <button
          type="button"
          className="mobile-menu-button"
          aria-label="Open sidebar"
          onClick={onMenuClick}
          style={{
            display: 'none',
            width: '40px',
            height: '40px',
            borderRadius: '12px',
            border: 'none',
            background: 'transparent',
            color: colors.textPrimary,
            cursor: 'pointer',
          }}
        >
          ☰
        </button>
        <h1
          className="font-display"
          style={{
            fontSize: '22px',
            fontWeight: 600,
            color: colors.textPrimary,
            marginBottom: '2px',
            lineHeight: 1.1,
            margin: 0,
          }}
        >
          {title}
        </h1>
        <div
          className="font-body"
          style={{
            fontSize: '13px',
            fontWeight: 400,
            color: colors.textMuted,
          }}
        >
          {subtitle}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        <button
          type="button"
          aria-label={soundMuted ? 'Unmute notification sounds' : 'Mute notification sounds'}
          title={soundMuted ? 'Sounds muted. Click to unmute' : 'Mute notification sounds'}
          onClick={() => setSoundMuted((m) => !m)}
          style={{
            border: `1px solid ${colors.border}`,
            background: colors.surface,
            color: soundMuted ? 'var(--color-error)' : colors.textPrimary,
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {soundMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
        <button
          type="button"
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={toggle}
          style={{
            border: `1px solid ${colors.border}`,
            background: colors.surface,
            color: colors.textPrimary,
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
            fontSize: '16px',
          }}
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        <div ref={bellRef} style={{ position: 'relative' }}>
          <button
            type="button"
            aria-label="Notifications"
            onClick={toggleBell}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              position: 'relative',
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Bell size={20} color={colors.textPrimary} />
            {unreadCount > 0 ? (
              <span
                className="font-body"
                style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-6px',
                  background: 'var(--color-error)',
                  color: colors.bgPrimary,
                  borderRadius: '999px',
                  fontSize: '9px',
                  fontWeight: 700,
                  padding: '2px 5px',
                  lineHeight: 1,
                }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            ) : null}
          </button>

          {bellOpen ? (
            <div
              style={{
                position: 'absolute',
                top: '40px',
                right: 0,
                width: '320px',
                maxHeight: '420px',
                overflowY: 'auto',
                background: colors.bgPrimary,
                border: `1px solid ${colors.border}`,
                borderRadius: '12px',
                boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
                zIndex: 1000,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 14px',
                  borderBottom: `1px solid ${colors.border}`,
                }}
              >
                <span className="font-body" style={{ fontWeight: 600, color: colors.textPrimary, fontSize: '14px' }}>
                  Notifications
                </span>
                {notifications.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => clearNotifications()}
                    style={{ border: 'none', background: 'transparent', color: colors.textMuted, fontSize: '12px', cursor: 'pointer' }}
                  >
                    Clear
                  </button>
                ) : null}
              </div>

              {notifications.length === 0 ? (
                <div
                  className="font-body"
                  style={{ padding: '22px 14px', color: colors.textMuted, fontSize: '13px', textAlign: 'center' }}
                >
                  No notifications yet
                </div>
              ) : (
                notifications.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => {
                      if (n.href) navigate(n.href)
                      setBellOpen(false)
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      border: 'none',
                      borderBottom: `1px solid ${colors.border}`,
                      background: 'transparent',
                      padding: '10px 14px',
                      cursor: 'pointer',
                    }}
                  >
                    <div className="font-body" style={{ color: colors.textPrimary, fontSize: '13px', fontWeight: 600 }}>
                      {n.title}
                    </div>
                    {n.body ? (
                      <div className="font-body" style={{ color: colors.textMuted, fontSize: '12px', marginTop: '2px' }}>
                        {n.body}
                      </div>
                    ) : null}
                    <div className="font-body" style={{ color: colors.textMuted, fontSize: '10px', marginTop: '3px' }}>
                      {new Date(n.at).toLocaleTimeString()}
                    </div>
                  </button>
                ))
              )}
            </div>
          ) : null}
        </div>

        <div
          className="font-body"
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '999px',
            background: colors.accent,
            color: colors.textPrimary,
            fontSize: '14px',
            fontWeight: 600,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {getInitials(user?.name)}
        </div>
      </div>
    </header>
  )
}
